import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { makeData, fC } from '@/dashboard/data';
import { ArrowLeft, Bell, AlertTriangle, TrendingUp, Send } from 'lucide-react';

const F = { mono:"'IBM Plex Mono',monospace", head:"'Outfit',sans-serif" };

export default function AlertsPage() {
  const { isDark, colors: T } = useTheme();
  const navigate = useNavigate();
  const [data] = useState(() => makeData());
  const [filter, setFilter] = useState('all');

  const alerts = [
    ...data.insights.map((ins, i) => ({
      id: `insight-${i}`, text: ins, time: `${Math.floor(Math.random()*59)+1}m ago`,
      type: ins.startsWith('CRITICAL') ? 'critical' : ins.startsWith('ALERT') ? 'alert' : 'win',
    })),
    ...data.blindSpots.map(b => ({
      id: `bs-${b.id}`, text: `BLIND SPOT: ${b.title} — Est. recovery +${fC(b.impact)}/mo`, time: 'Today',
      type: b.sev === 'critical' ? 'critical' : 'alert', action: b.action,
    })),
  ].sort((a, b) => {
    const order = { critical: 0, alert: 1, win: 2 };
    return (order[a.type] || 3) - (order[b.type] || 3);
  });

  const filtered = filter === 'all' ? alerts : alerts.filter(a => a.type === filter);
  const colors = { critical: '#FF4444', alert: '#FFD166', win: '#06D6A0' };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-zinc-50'}`} style={{ fontFamily: F.head }}>
      <div style={{ background: isDark ? 'rgba(7,9,15,.98)' : 'rgba(255,255,255,.95)', borderBottom: `1px solid ${T.border}`,
        padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 100, backdropFilter: 'blur(12px)' }}>
        <button data-testid="alerts-back" onClick={() => navigate('/dashboard')}
          style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: T.muted, cursor: 'pointer', fontSize: 13 }}>
          <ArrowLeft size={16} /> Dashboard
        </button>
        <div style={{ flex: 1 }} />
        <Bell size={18} color="#FF4444" />
        <span style={{ fontSize: 16, fontWeight: 800 }}>Alerts & Insights</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12, fontFamily: F.mono, color: T.muted }}>{alerts.length} total</span>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '16px 16px 100px' }}>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
          {['all', 'critical', 'alert', 'win'].map(f => (
            <button key={f} data-testid={`alert-filter-${f}`} onClick={() => setFilter(f)}
              style={{ padding: '6px 14px', borderRadius: 5, fontSize: 12, fontFamily: F.mono, cursor: 'pointer', fontWeight: filter === f ? 700 : 400,
                border: filter === f ? `1px solid ${colors[f] || '#00D4FF'}` : `1px solid ${T.border}`,
                background: filter === f ? `${colors[f] || '#00D4FF'}14` : 'transparent',
                color: filter === f ? (colors[f] || '#00D4FF') : T.muted }}>
              {f === 'all' ? `All (${alerts.length})` : `${f.charAt(0).toUpperCase() + f.slice(1)} (${alerts.filter(a => a.type === f).length})`}
            </button>
          ))}
        </div>

        {filtered.map(a => (
          <div key={a.id} data-testid={`alert-${a.id}`}
            style={{ display: 'flex', gap: 12, padding: '14px 16px', marginBottom: 6, borderRadius: 8,
              background: isDark ? '#0d111a' : '#fff', border: `1px solid ${T.border}`, borderLeft: `3px solid ${colors[a.type]}` }}>
            <div style={{ flexShrink: 0, marginTop: 2 }}>
              {a.type === 'critical' ? <AlertTriangle size={16} color="#FF4444" /> :
               a.type === 'win' ? <TrendingUp size={16} color="#06D6A0" /> :
               <Bell size={16} color="#FFD166" />}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.5 }}>{a.text}</div>
              {a.action && <div style={{ fontSize: 12, color: '#00D4FF', marginTop: 4 }}>Action: {a.action}</div>}
              <div style={{ fontSize: 11, color: T.muted, fontFamily: F.mono, marginTop: 4 }}>{a.time}</div>
            </div>
            <button onClick={() => {
              const text = `ALERT: ${a.text}${a.action ? `\nAction: ${a.action}` : ''}\n\n- OmniView`;
              if (navigator.share) navigator.share({ title: 'OmniView Alert', text });
              else window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
            }} style={{ padding: 6, borderRadius: 4, border: `1px solid ${T.border}`, background: 'none', color: T.muted, cursor: 'pointer', flexShrink: 0, alignSelf: 'center' }}>
              <Send size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
